import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	type ArtemisEnvironmentFilterInput,
	type ArtemisEnvironmentSortInput,
	CreateEnvironmentDocument,
	type CreateEnvironmentInput,
	DeleteEnvironmentDocument,
	EnvironmentConnectivityDocument,
	type EnvironmentConnectivityQuery,
	GetEnvironmentDeleteImpactDocument,
	type GetEnvironmentDeleteImpactQuery,
	GetEnvironmentDocument,
	GetEnvironmentSetupTemplateDocument,
	type GetEnvironmentSetupTemplateQueryVariables,
	GetEnvironmentsDocument,
	UpdateEnvironmentDocument,
	type UpdateEnvironmentInput,
} from "@/common/artemis-sdk/graphql";
import type { Environment } from "../model";
import { useGqlClient } from "../use-gql-client";
import { buildWhere, toOrder, usePagedList } from "./use-paged-list";

/** The result of an on-demand environment connectivity check. */
export type EnvironmentConnectivityResult =
	EnvironmentConnectivityQuery["environmentConnectivity"];

/**
 * Fetches the first page of environments for a workspace (array form, for lookups/dropdowns).
 * For the paginated list use {@link useEnvironmentsPaged}.
 */
export function useEnvironments(workspaceId: string) {
	const gqlFetch = useGqlClient();

	return useQuery({
		queryKey: ["environments", workspaceId],
		queryFn: async () => {
			const data = await gqlFetch(GetEnvironmentsDocument, { workspaceId });
			return (data.environments?.nodes as Environment[]) ?? [];
		},
		enabled: !!workspaceId,
	});
}

/**
 * Server-side paginated environment list for a workspace: Next/Prev cursor pagination plus
 * server-side search (name/id), status/provider/classification filters, and sort.
 */
export function useEnvironmentsPaged(workspaceId: string) {
	const gqlFetch = useGqlClient();

	return usePagedList<Environment>({
		baseKey: ["environments", workspaceId],
		enabled: !!workspaceId,
		initialSort: [{ id: "createdAt", desc: true }],
		fetchPage: async ({ debouncedSearch, filters, sort, window }) => {
			const data = await gqlFetch(GetEnvironmentsDocument, {
				workspaceId,
				...window,
				order: toOrder<ArtemisEnvironmentSortInput>(sort),
				where: buildWhere(debouncedSearch, ["name", "id"], filters) as
					| ArtemisEnvironmentFilterInput
					| undefined,
			});
			const c = data.environments;
			return {
				nodes: (c?.nodes as Environment[]) ?? [],
				totalCount: c?.totalCount ?? 0,
				pageInfo: c?.pageInfo ?? { hasNextPage: false, hasPreviousPage: false },
			};
		},
	});
}

/**
 * Fetches a single environment by its ID.
 */
export function useEnvironment(environmentId: string) {
	const gqlFetch = useGqlClient();

	return useQuery({
		queryKey: ["environment", environmentId],
		queryFn: async () => {
			const data = await gqlFetch(GetEnvironmentDocument, {
				environmentId,
			});
			return data.environment as Environment;
		},
		enabled: !!environmentId,
	});
}

/**
 * Generates the tailored onboarding template (CloudFormation for AWS) on demand. The template is
 * baked from the wizard selections — external id, enabled capabilities, role names, and the operator
 * action sets — so it's an imperative call (download on click), not a cached query.
 */
export function useGenerateSetupTemplate() {
	const gqlFetch = useGqlClient();

	const { mutateAsync, isPending, error } = useMutation({
		mutationFn: async (
			variables: GetEnvironmentSetupTemplateQueryVariables,
		) => {
			const data = await gqlFetch(
				GetEnvironmentSetupTemplateDocument,
				variables,
			);
			return data.environmentSetupTemplate;
		},
	});
	return { generate: mutateAsync, isPending, error };
}

/** Creates a new environment. */
export function useCreateEnvironment() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: async (input: CreateEnvironmentInput) => {
			const data = await gqlFetch(CreateEnvironmentDocument, { input });
			return data.createEnvironment.environment;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["organizationMetrics"] });
		},
	});
	return mutate;
}

/** Updates an existing environment. Invalidates the environment query on success. */
export function useUpdateEnvironment(environmentId: string) {
	const gqlFetch = useGqlClient();

	const queryClient = useQueryClient();
	const { mutate } = useMutation({
		mutationFn: async (input: UpdateEnvironmentInput) => {
			const data = await gqlFetch(UpdateEnvironmentDocument, {
				environmentId,
				input,
			});
			return data.updateEnvironment.environment;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["environment", environmentId],
			});
		},
	});
	return mutate;
}

/**
 * Runs an on-demand connectivity check for an environment: the platform tries to assume each
 * configured role and returns a per-role pass/fail. Live only, nothing is persisted. Returns the
 * imperative trigger plus pending/error state, following the {@link useRefreshDeployment} pattern.
 */
export function useVerifyEnvironmentConnection(environmentId: string) {
	const gqlFetch = useGqlClient();

	const { mutate, isPending, error } = useMutation({
		mutationFn: async () => {
			const data = await gqlFetch(EnvironmentConnectivityDocument, {
				environmentId,
			});
			return data.environmentConnectivity;
		},
	});
	return { verify: mutate, isPending, error };
}

/** What deleting an environment would touch (deployments it takes along, and what blocks it). */
export type EnvironmentDeleteImpact = NonNullable<
	GetEnvironmentDeleteImpactQuery["environmentDeleteImpact"]
>;

/**
 * Returns a function that fetches, fresh, what deleting the environment would touch: the deployments
 * deleted along with it, and the managed agents, jobs and task templates that block it. Same check
 * the backend enforces on delete.
 */
export function useFetchEnvironmentDeleteImpact(environmentId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return () =>
		queryClient.fetchQuery({
			queryKey: ["environmentDeleteImpact", environmentId],
			queryFn: async () => {
				const data = await gqlFetch(GetEnvironmentDeleteImpactDocument, {
					environmentId,
				});
				return data.environmentDeleteImpact ?? null;
			},
			staleTime: 0,
		});
}

/**
 * Deletes an environment (and, server-side, its deployments). Invalidates the environment, the
 * environment lists, and the agents/projects that referenced it on success.
 */
export function useDeleteEnvironment(environmentId: string) {
	const gqlFetch = useGqlClient();

	const queryClient = useQueryClient();
	const { mutate } = useMutation({
		mutationFn: async () => {
			const data = await gqlFetch(DeleteEnvironmentDocument, {
				environmentId,
			});
			return data.deleteEnvironment.success;
		},
		onSuccess: () => {
			for (const key of [
				"environment",
				"environments",
				"agents",
				"agent",
				"project",
				"projects",
			]) {
				queryClient.invalidateQueries({ queryKey: [key] });
			}
		},
	});
	return mutate;
}
